/** Thin fetch wrapper. Same-origin in dev via the Vite proxy, so cookies just work. */

export class ApiError extends Error {
  /**
   * The parsed error body, when there was one. Most callers only want the
   * message, but a refusal sometimes carries the fact that makes it actionable
   * -- "results are published after the exam closes" is far more useful with the
   * `availableAt` that came with it.
   */
  constructor(message: string, public status: number, public body?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: init?.body ? { "content-type": "application/json" } : undefined,
    ...init,
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let body: Record<string, unknown> | undefined;
    try {
      body = await res.json();
      if (typeof body?.error === "string") message = body.error;
    } catch {
      // Non-JSON error body; keep the generic message.
    }
    throw new ApiError(message, res.status, body);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ------------------------------------------------------------------- types

export type UserRole = "STUDENT" | "TEACHER" | "UNIVERSITY_ADMIN" | "SUPER_ADMIN";

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  rollNumber: string | null;
  universityId: string | null;
}

/**
 * Course-level staff. A super admin is not staff: they run the platform and
 * have no course, assignment or exam of their own to see.
 */
export const isStaffRole = (r: UserRole) => r === "TEACHER" || r === "UNIVERSITY_ADMIN";
export const isAdminRole = (r: UserRole) => r === "UNIVERSITY_ADMIN" || r === "SUPER_ADMIN";

export interface University {
  id: string;
  name: string;
  code: string;
  allowedEmailDomain: string;
  active: boolean;
  createdAt: string;
  userCount: number;
  courseCount: number;
  adminCount: number;
  teacherCount: number;
  studentCount: number;
}

/** An institution's administrator, as the super admin sees them. */
export interface InstitutionAdmin {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  active: boolean;
  createdAt: string;
}

export interface FacultyMember {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  active: boolean;
  createdAt: string;
  _count?: { createdCourses: number };
}

// ------------------------------------------------- institution console

export interface InstitutionOverview {
  counts: {
    courses: number;
    archivedCourses: number;
    teachers: number;
    students: number;
    liveExams: number;
    attemptsInProgress: number;
    submissionsToday: number;
    unreviewed: number;
  };
  attention: {
    withoutTeacher: { id: string; code: string; year: number; semester: number }[];
    withoutContent: { id: string; code: string; year: number; semester: number }[];
    unbatchedStudents: number;
  };
}

export interface InstitutionCourse {
  id: string;
  code: string;
  name: string;
  year: number;
  semester: number;
  archived: boolean;
  owner: { id: string; name: string; email: string };
  teachers: { id: string; name: string; active: boolean }[];
  studentCount: number;
  assignmentCount: number;
  examCount: number;
  batchCount: number;
  unreviewedCount: number;
}

export interface CourseDetail {
  course: {
    id: string;
    code: string;
    name: string;
    year: number;
    semester: number;
    archived: boolean;
    owner: { id: string; name: string; email: string };
    batches: { id: string; name: string; scheduleNote: string; studentCount: number }[];
  };
  teachers: { id: string; name: string; email: string; active: boolean }[];
  studentCount: number;
  unbatchedCount: number;
  assignments: {
    id: string;
    title: string;
    published: boolean;
    dueAt: string | null;
    maxMarks: number;
    visibility: string;
    submissionCount: number;
    testCaseCount: number;
    pendingCount: number;
  }[];
  exams: {
    id: string;
    title: string;
    published: boolean;
    opensAt: string;
    closesAt: string;
    durationMinutes: number | null;
    questionCount: number;
    attemptCount: number;
  }[];
}

export interface MonitoredExam {
  id: string;
  title: string;
  published: boolean;
  opensAt: string;
  closesAt: string;
  durationMinutes: number | null;
  questionCount: number;
  maxViolations: number;
  onViolationExceeded: string;
  course: { id: string; code: string; name: string; year: number; semester: number };
  eligible: number;
  inProgress: number;
  submitted: number;
  autoSubmitted: number;
  expired: number;
  locked: number;
  notStarted: number;
  flaggedAttempts: number;
  worstViolations: number;
}

export interface ExamMonitor {
  live: MonitoredExam[];
  upcoming: MonitoredExam[];
  recent: MonitoredExam[];
  drafts: MonitoredExam[];
  now: string;
}

export interface FlaggedAttempt {
  id: string;
  status: string;
  violationCount: number;
  startedAt: string;
  submittedAt: string | null;
  ipAtStart: string;
  overThreshold: boolean;
  student: { id: string; name: string; email: string; rollNumber: string | null };
  exam: {
    id: string;
    title: string;
    maxViolations: number;
    onViolationExceeded: string;
    course: { id: string; code: string };
  };
}

export interface MarkingRow {
  id: string;
  title: string;
  dueAt: string | null;
  maxMarks: number;
  course: { id: string; code: string; year: number; semester: number };
  teachers: { id: string; name: string }[];
  submissionCount: number;
  pendingCount: number;
  oldestPendingAt: string | null;
  pastDue: boolean;
}

export interface AuditEntry {
  id: string;
  action: string;
  targetType: string;
  targetId: string;
  details: string;
  ip: string;
  createdAt: string;
  actor: { name: string; email: string; role: string };
}

// ------------------------------------------------------------------- exams

export interface ExamSummary {
  id: string;
  title: string;
  opensAt: string;
  closesAt: string;
  durationMinutes: number | null;
  published: boolean;
  questionCount: number;
  attemptCount: number;
  flaggedAttempts?: number;
  myAttempt: { id: string; status: string; finalScore: number | null } | null;
}

export interface Exam {
  id: string;
  courseId: string;
  title: string;
  description: string;
  published: boolean;
  opensAt: string;
  closesAt: string;
  durationMinutes: number | null;
  questionsToServe: number | null;
  shuffleQuestions: boolean;
  requireFullscreen: boolean;
  blockCopyPaste: boolean;
  maxViolations: number;
  onViolationExceeded: string;
  showResultsToStudent: string;
  captureOnFocusLoss: boolean;
}

/**
 * Autosaved work in progress. `code` for a programming question,
 * `selectedOptions` (comma-separated ids) for a multiple-choice one.
 */
export interface Draft {
  code: string;
  selectedOptions: string;
  updatedAt?: string;
}

/**
 * A student's own result. Marks only -- the server does not send test cases,
 * expected output or the answer key back with it.
 */
export interface ExamResult {
  exam: { id: string; title: string; showResultsToStudent: string };
  attempt: {
    id: string;
    status: string;
    startedAt: string;
    submittedAt: string | null;
    finalScore: number | null;
    violationCount: number;
  };
  totalMarks: number;
  /** `scored: null` means unanswered, which is not the same fact as zero. */
  questions: { title: string; kind: QuestionKind; marks: number; scored: number | null }[];
}

export interface ExamQuestion {
  id: string;
  examId: string;
  assignmentId: string;
  marks: number;
  orderIndex: number;
  assignment: {
    id: string;
    title: string;
    kind?: QuestionKind;
    _count?: { testCases: number; options: number };
    options?: QuestionOption[];
    expectedAnswers?: number;
    description?: string;
    inputFormat?: string;
    outputFormat?: string;
    starterCode?: string;
    language: string;
    timeLimitMs?: number;
    /** Visible cases only -- the worked example. */
    testCases?: TestCase[];
  };
}

export interface ExamAttempt {
  id: string;
  status: string;
  startedAt: string;
  deadlineAt: string;
  submittedAt?: string | null;
  finalScore?: number | null;
  violationCount: number;
}

export interface AttemptRow {
  id: string;
  student: { id: string; name: string; email: string; rollNumber: string | null };
  status: string;
  startedAt: string;
  deadlineAt: string;
  submittedAt: string | null;
  finalScore: number | null;
  violationCount: number;
  /** What happened and when: TAB_SWITCH, BLUR, FULLSCREEN_EXIT, COPY, PASTE. */
  violations: { kind: string; at: string }[];
  screenShareState?: string;
  answered: number;
  ipAtStart: string;
}

export interface Course {
  id: string;
  name: string;
  code: string;
  year: number;
  semester: number;
  role: string;
  batchName: string | null;
  archived: boolean;
  /** Sheets and standalone questions; parts are not counted separately. */
  assignmentCount: number;
  examCount: number;
  studentCount: number;
}

export interface Batch {
  id: string;
  name: string;
  scheduleNote: string;
  studentCount: number;
}

/** "" when the author never rated it. */
export type Difficulty = "" | "EASY" | "MEDIUM" | "HARD";

export interface LibraryQuestion {
  id: string;
  title: string;
  description: string;
  language: string;
  maxMarks: number;
  difficulty: Difficulty;
  /** Free text, e.g. "Loops". Empty when nobody has tagged it. */
  topic: string;
  createdAt: string;
  _count: { testCases: number };
  /** Null for a stock question: it belongs to the institution, not a course. */
  course: { id: string; code: string; name: string; year: number; semester: number } | null;
}

/**
 * A library question opened for preview: everything needed to judge whether it
 * is worth importing, including the hidden test cases.
 */
export interface LibraryQuestionDetail {
  id: string;
  title: string;
  kind: QuestionKind;
  difficulty: Difficulty;
  topic: string;
  description: string;
  inputFormat: string;
  outputFormat: string;
  starterCode: string;
  language: string;
  maxMarks: number;
  timeLimitMs: number;
  course: { id: string; code: string; name: string; year: number; semester: number } | null;
  testCases: TestCase[];
  options: QuestionOption[];
}

/**
 * Where an imported question can land.
 *
 * A library question is a question, so the places it can go are the containers
 * that hold questions: a sheet, an exam paper, or -- for a one-question
 * assignment -- the course itself.
 */
export type ImportDestination = { courseId: string } | { sheetId: string } | { examId: string };

/** A course a teacher could import into, with its containers. */
export interface ImportTargetCourse {
  id: string;
  code: string;
  name: string;
  year: number;
  semester: number;
  sheets: { id: string; title: string; questionCount: number }[];
  /** `frozen` once a student has sat it: the paper can no longer change. */
  exams: { id: string; title: string; questionCount: number; frozen: boolean }[];
}

export interface ImportResult {
  imported: { id: string; sourceId: string; title: string; testCasesCopied: number }[];
  skipped: { sourceId: string; reason: string }[];
}

/**
 * Copy library questions somewhere. Rejects if nothing landed, with the
 * per-question reasons on the error body.
 */
export const importFromLibrary = (into: ImportDestination, ids: string[]) =>
  api.post<ImportResult>("/api/library/import", { into, ids });

/** Label and CSS modifier for a difficulty, including the unrated case. */
export const difficultyLabel = (d: Difficulty) =>
  d === "EASY" ? "Easy" : d === "MEDIUM" ? "Medium" : d === "HARD" ? "Hard" : "Unrated";

/** Human label for a course offering, e.g. "2026 · Sem 1". */
export const termLabel = (c: { year: number; semester: number }) => `${c.year} · Sem ${c.semester}`;

export interface AssignmentSummary {
  id: string;
  title: string;
  kind?: QuestionKind;
  maxMarks: number;
  dueAt: string | null;
  published: boolean;
  language: string;
  testCaseCount: number;
  /** Questions inside this sheet; 0 means it is a single question. */
  partCount?: number;
  /** Parts with neither test cases nor options, for staff. */
  unfinishedParts?: number;
  answeredParts?: number;
  latestSubmission: { status: string; score: number | null; submittedAt: string | null } | null;
}

/** One question inside a sheet. */
export interface AssignmentPart {
  id: string;
  title: string;
  kind: QuestionKind;
  maxMarks: number;
  orderIndex: number;
  testCaseCount: number;
  optionCount: number;
  unfinished: boolean;
  myLatest: { status: string; score: number | null; submittedAt: string } | null;
}

export interface TestCase {
  id: string;
  name: string;
  hidden: boolean;
  weight: number;
  orderIndex: number;
  stdin?: string;
  expectedStdout?: string;
  matchMode?: string;
  tolerance?: number;
}

export type QuestionKind = "PROGRAMMING" | "MCQ";

/**
 * A marking criterion for AI-assisted partial credit. Staff-only — a rubric
 * describes what a correct approach looks like, which is a partial answer key.
 */
export interface RubricCriterion {
  id: string;
  text: string;
  marks: number;
  orderIndex: number;
}

export interface Rubric {
  criteria: RubricCriterion[];
  /** 0 means AI credit is off for this question. */
  aiCreditCapPercent: number;
  rubricVersion: number;
  maxMarks: number;
  /** False when the server has no API key configured at all. */
  enabled: boolean;
}

/** A model's proposed partial credit for one submission, awaiting a decision. */
export interface AiReview {
  /** Marks to ADD to the auto-score, never a replacement for it. */
  aiSuggestedScore: number | null;
  aiRationale: string;
  /** JSON: per-criterion awards, so the total can be shown as its parts. */
  aiCriteria: string;
  aiStatus: "NONE" | "SUGGESTED" | "ACCEPTED" | "REJECTED";
  /** The model was unsure — a human should look at this one. */
  aiFlagged: boolean;
  aiModel: string;
  aiRubricVersion: number | null;
  aiGeneratedAt: string | null;
}

export interface AiReviewResponse {
  review: AiReview;
  autoScore: number;
  maxCredit: number;
  proposedTotal: number;
  maxMarks: number;
  criteria: { id: string; text: string; marks: number }[];
}

/** One criterion's award, as stored in `AiReview.aiCriteria`. */
export interface AiAward {
  criterionId: string;
  awarded: number;
  met: boolean;
  note: string;
}

/** One choice on an MCQ. `correct` is present only for staff. */
export interface QuestionOption {
  id: string;
  text: string;
  orderIndex: number;
  correct?: boolean;
}

export interface Assignment {
  id: string;
  courseId: string;
  title: string;
  kind: QuestionKind;
  options: QuestionOption[];
  /** How many options are correct: decides radio vs checkbox, not which. */
  expectedAnswers: number;
  description: string;
  inputFormat: string;
  outputFormat: string;
  starterCode: string;
  language: string;
  maxMarks: number;
  difficulty: Difficulty;
  topic: string;
  dueAt: string | null;
  allowLate: boolean;
  published: boolean;
  timeLimitMs: number;
  memoryLimitKb: number;
  outputLimitKb: number;
  libraryVisible: boolean;
  sourceAssignmentId: string | null;
  testCases: TestCase[];
  parts: AssignmentPart[];
  parent: {
    id: string;
    title: string;
    dueAt: string | null;
    allowLate: boolean;
    published: boolean;
  } | null;
  parentId: string | null;
  course: { id: string; name: string; code: string };
}

export interface RunResult {
  stdout: string;
  stderr: string;
  exitCode: number | null;
  signal: string | null;
  timedOut: boolean;
  outputTruncated: boolean;
  timeMs: number;
}

export interface TestOutcome {
  name: string;
  hidden: boolean;
  passed: boolean;
  weight: number;
  timeMs: number;
  reason?: string;
  actualStdout?: string;
  stderr?: string;
  exitCode?: number | null;
  signal?: string | null;
}

export interface SubmitResponse {
  submission: { id: string; submittedAt: string; status: string; autoScore: number | null; late: boolean };
  compiled: boolean;
  compilerOutput: string;
  score: number;
  maxMarks: number;
  tests: TestOutcome[];
}

/** One test case checked against a teacher's reference solution. */
export interface ValidationResult {
  testCaseId: string;
  name: string;
  hidden: boolean;
  agrees: boolean;
  stdin: string;
  expectedStdout: string;
  actualStdout: string;
  stderr: string;
  exitCode: number | null;
  signal: string | null;
  timedOut: boolean;
  timeMs: number;
  reason?: string;
}

export interface ValidationReport {
  compiled: boolean;
  compilerOutput: string;
  results: ValidationResult[];
}

export interface AssignmentSummaryStats {
  maxMarks: number;
  rosterCount: number;
  submittedCount: number;
  notSubmitted: {
    id: string;
    name: string;
    email: string;
    rollNumber: string | null;
    batchName: string | null;
  }[];
  average: number | null;
  median: number | null;
  highest: number | null;
  lowest: number | null;
  fullMarks: number;
  zero: number;
  lateCount: number;
  awaitingReview: number;
}

/** A student's own marks across one course — every assessment in one place. */
/**
 * A student's whole record: every course they have taken and what they earned.
 *
 * `overall` sums marks across courses because that is a fact about work done —
 * it is not a grade, and the page says so. How a college weighs one course
 * against another is theirs to decide.
 */
export interface DegreeMarks {
  courses: {
    id: string;
    code: string;
    name: string;
    year: number;
    semester: number;
    archived: boolean;
    batchName: string | null;
    totals: CourseMarks["totals"];
  }[];
  overall: {
    earned: number;
    outOf: number;
    percent: number | null;
    courseCount: number;
    assessedCourseCount: number;
    pendingCount: number;
    outstandingCount: number;
  } | null;
}

export interface CourseMarks {
  course: { id: string; name: string; code: string; archived: boolean };
  assignments: {
    kind: "ASSIGNMENT";
    id: string;
    title: string;
    questionCount: number;
    answered: number;
    outOf: number;
    /** Null until something is submitted; not the same fact as a zero. */
    earned: number | null;
    dueAt: string | null;
    submittedAt: string | null;
    late: boolean;
    awaitingMark: boolean;
  }[];
  exams: {
    kind: "EXAM";
    id: string;
    title: string;
    opensAt: string;
    closesAt: string;
    status: string | null;
    earned: number | null;
    outOf: number | null;
    /** Null when never sat. Otherwise the server's release decision. */
    resultState: "AVAILABLE" | "NEVER" | "PENDING" | null;
    availableAt: string | null;
  }[];
  totals: {
    earned: number;
    outOf: number;
    percent: number | null;
    assessedCount: number;
    outstandingCount: number;
    pendingCount: number;
  };
}

/**
 * How a question behaved, over each student's latest submission: which test
 * cases fail, how long they take, where the marks fell.
 */
export interface QuestionAnalytics {
  assignment: {
    id: string;
    title: string;
    kind: QuestionKind;
    maxMarks: number;
    timeLimitMs: number;
  };
  summary: {
    rosterCount: number;
    attemptedCount: number;
    submissionCount: number;
    compileErrors: number;
    fullMarks: number;
    zero: number;
    average: number | null;
  };
  distribution: { from: number; to: number; count: number }[];
  /** Worst pass rate first. `ran` excludes submissions that never compiled. */
  testCases: {
    id: string;
    name: string;
    hidden: boolean;
    weight: number;
    ran: number;
    passed: number;
    passRate: number | null;
    timedOut: number;
    meanTimeMs: number | null;
    maxTimeMs: number | null;
  }[];
  options: { id: string; text: string; correct: boolean; chosen: number }[];
}

/**
 * The marking grid for a lab sheet: students down, its questions across.
 *
 * A cell is the student's latest submission for that question, not their best --
 * it is the one the Review link opens and the one the CSV records.
 */
export interface SheetMarks {
  sheet: { id: string; title: string };
  parts: { id: string; title: string; maxMarks: number; kind: QuestionKind; orderIndex: number }[];
  rows: {
    student: { id: string; name: string; email: string; rollNumber: string | null };
    batchName: string | null;
    cells: {
      partId: string;
      submissionId: string | null;
      /** Null means nothing was submitted, which is not a zero. */
      score: number | null;
      manual: boolean;
      status: string | null;
      late: boolean;
      submittedAt: string | null;
    }[];
    answered: number;
    total: number | null;
  }[];
  summary: {
    sheetMaxMarks: number;
    rosterCount: number;
    startedCount: number;
    completedCount: number;
    awaitingReview: number;
    average: number | null;
    median: number | null;
    highest: number | null;
    lowest: number | null;
  };
}

export interface ExamSchedule {
  id: string;
  batchId: string;
  opensAt: string;
  closesAt: string;
}

/**
 * One submission in full, from `GET /api/submissions/:id`.
 *
 * Served to the course's staff and to the student who wrote it. A student
 * reviewing their own answer gets the options without the key, and hidden test
 * cases arrive with their I/O blanked -- the server decides that, not the page.
 */
export interface SubmissionDetail {
  id: string;
  code: string;
  status: string;
  compilerOutput: string;
  autoScore: number | null;
  manualScore: number | null;
  teacherComment: string | null;
  late: boolean;
  submittedAt: string;
  assignment: { id: string; title: string; maxMarks: number; kind?: string };
  student: { id: string; name: string; email: string; rollNumber: string | null };
  testResults: {
    id: string;
    passed: boolean;
    actualStdout: string;
    stderr: string;
    exitCode: number | null;
    signal: string | null;
    timedOut: boolean;
    timeMs: number;
    testCase: { name: string; hidden: boolean; weight: number };
  }[];
  options: QuestionOption[];
  selectedOptionIds: string[];
}

export interface SubmissionSummary {
  id: string;
  submittedAt: string;
  status: string;
  autoScore: number | null;
  manualScore: number | null;
  teacherComment: string | null;
  late: boolean;
  student?: { id: string; name: string; email: string; rollNumber: string | null };
}
